import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssessmentDetail,
  Paginated,
  ResponseListItem,
  ResponseTransition,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  LoadingPane,
  PageHeader,
  ProgressBar,
  StatusBadge,
  Table,
  Textarea,
  formatDate,
  useToast,
} from '../../components/ui.js';

export function ReviewQueuePage() {
  const { assessmentId = '' } = useParams();
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const role = org.role;

  const detail = useQuery({
    queryKey: ['assessment', assessmentId],
    queryFn: () => apiFetch<AssessmentDetail>(`/assessments/${assessmentId}`),
  });
  const responses = useQuery({
    queryKey: ['responses', assessmentId],
    queryFn: () =>
      apiFetch<Paginated<ResponseListItem>>(`/assessments/${assessmentId}/responses`, {
        query: { limit: 500 },
      }),
  });

  const [noteFor, setNoteFor] = useState<ResponseListItem | null>(null);
  const [note, setNote] = useState('');
  const transition = useMutation({
    mutationFn: ({ id, action, actionNote }: { id: string; action: ResponseTransition; actionNote?: string }) =>
      apiFetch(`/responses/${id}/transition`, { method: 'POST', body: { action, note: actionNote } }),
    onSuccess: (_, vars) => {
      toast(`Done: ${vars.action.replaceAll('_', ' ')}.`, 'success');
      setNoteFor(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['responses', assessmentId] });
      void queryClient.invalidateQueries({ queryKey: ['assessment', assessmentId] });
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Action failed', 'error'),
  });

  if (detail.isLoading || responses.isLoading) return <LoadingPane label="Loading review queue" />;
  if (!detail.data) return null;
  const items = responses.data?.items ?? [];
  const progress = detail.data.progress;

  const underReview = items.filter((r) => r.status === 'under_review');
  const changesRequested = items.filter((r) => r.status === 'changes_requested');
  const awaitingAssessment = items.filter((r) => r.status === 'finalised');
  const assessed = items.filter((r) => ['assessed', 'locked'].includes(r.status));

  const section = (
    title: string,
    rows: ResponseListItem[],
    actions?: (r: ResponseListItem) => React.ReactNode,
  ) => (
    <Card title={`${title} (${rows.length})`}>
      {rows.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Nothing here right now.</p>
      ) : (
        <Table
          head={
            <>
              <th>Clause</th>
              <th>Question</th>
              <th>Status</th>
              <th>Evidence</th>
              <th>Updated</th>
              {actions && <th />}
            </>
          }
        >
          {rows.map((r) => (
            <tr key={r.id}>
              <td style={{ whiteSpace: 'nowrap' }}>{r.clauseNumber}</td>
              <td>
                <Link to={`/assessments/${assessmentId}/responses/${r.id}`}>
                  {r.questionText.length > 100 ? `${r.questionText.slice(0, 100)}…` : r.questionText}
                </Link>
              </td>
              <td>
                <StatusBadge status={r.status} reviewState={r.reviewState} />
              </td>
              <td>
                {r.requiredEvidence && r.evidenceCount === 0 ? (
                  <Badge tone="warning">missing</Badge>
                ) : (
                  r.evidenceCount || '—'
                )}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.updatedAt)}</td>
              {actions && <td style={{ whiteSpace: 'nowrap' }}>{actions(r)}</td>}
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );

  return (
    <div>
      <p>
        <Link to={`/assessments/${assessmentId}`}>← Back to assessment</Link>
      </p>
      <PageHeader
        title="Review queue"
        subtitle={
          <>
            {detail.data.name} · {progress.completionPercent}% complete
          </>
        }
      />
      <div style={{ maxWidth: 420, marginBottom: 24 }}>
        <ProgressBar percent={progress.completionPercent} label="Assessment completion" />
      </div>

      {(role === 'reviewer' || role === 'admin') && (
        <>
          {section('Awaiting review', underReview, (r) => (
            <span style={{ display: 'inline-flex', gap: 6 }}>
              <Button
                size="sm"
                loading={transition.isPending}
                onClick={() => transition.mutate({ id: r.id, action: 'approve' })}
              >
                Approve
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setNoteFor(r)}>
                Request changes
              </Button>
            </span>
          ))}
          {section('Changes requested — waiting on authors', changesRequested)}
        </>
      )}

      {role === 'assessor' && (
        <>
          {section('Awaiting assessment', awaitingAssessment, (r) => (
            <Button
              size="sm"
              loading={transition.isPending}
              onClick={() => transition.mutate({ id: r.id, action: 'mark_assessed' })}
            >
              Mark assessed
            </Button>
          ))}
          {section('Assessed', assessed)}
        </>
      )}

      {role === 'contributor' && (
        <>
          {section('Changes requested — needs your attention', changesRequested)}
          {section('With the reviewer', underReview)}
        </>
      )}

      {role !== 'assessor' && awaitingAssessment.length > 0 && role !== 'contributor' && (
        section('Finalised — awaiting assessor', awaitingAssessment)
      )}

      {items.length === 0 && (
        <EmptyState title="No responses" message="This assessment has no responses yet." />
      )}

      <Dialog open={!!noteFor} onClose={() => setNoteFor(null)} title="Request changes">
        <p style={{ color: 'var(--color-text-muted)' }}>{noteFor?.questionText}</p>
        <Field label="What needs to change?" hint="Recorded in the audit trail">
          {(props) => <Textarea {...props} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setNoteFor(null)}>
            Cancel
          </Button>
          <Button
            loading={transition.isPending}
            onClick={() =>
              noteFor &&
              transition.mutate({
                id: noteFor.id,
                action: 'request_changes',
                actionNote: note.trim() || undefined,
              })
            }
          >
            Request changes
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
